// Material Dashboard 2 React components
import { Grid, IconButton, Divider, Chip } from "@mui/material";
import MDBox from "components/MDBox";

// Components
import pxToRem from "assets/theme/functions/pxToRem";
import ConfigDropdown from "components/Dropdown/ConfigDropdown";
import DateTime from "components/DateTime/DateTime";
import FTextField from "components/Form/FTextField";
import MDButton from "components/MDButton";

// libraries
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import PropTypes from "prop-types";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { suggestedLocationsThunk } from "redux/Thunks/Equipment";

// utils
import Constants, { defaultData, Common, BackendFrontend, Icons } from "utils/Constants";
import MDTypography from "components/MDTypography";
import { useEffect, useMemo, useRef, useState } from "react";

const LOCATION_QR_FIELD_ID = "locationQRNumber";
const ALPHANUMERIC_LOCATION_FIELD_IDS = [
  "equipmentRow",
  "equipmentShelf",
  "equipmentSection",
  "warehouseCode",
];

// Fields that belong to a single warehouse allocation. Anything else stays equipment-level.
const PER_WAREHOUSE_FIELD_IDS = [
  Common.WAREHOUSE,
  "equipmentRow",
  "equipmentShelf",
  "equipmentSection",
  LOCATION_QR_FIELD_ID,
  Common.QUANTITY,
  Common.CONDITION,
  "reasonStatus",
  "reason",
];

// The fields that together identify one physical slot, and so decide whether two entries clash
const POSITION_FIELD_IDS = [Common.WAREHOUSE, "equipmentRow", "equipmentShelf", "equipmentSection"];

// Fields whose visibility depends on another field's value within the same entry
const DEPENDENT_PARENTS = [Common.CONDITION, "reasonStatus"];

export const emptyWarehouseEntry = () => ({
  warehouse: "",
  equipmentRow: "",
  equipmentShelf: "",
  equipmentSection: "",
  quantity: 1,
  condition: "ok",
  reasonStatus: "",
  reason: "",
});

/**
 * Visibility is derived from the entry's own values rather than stored on the shared field
 * definitions, so each warehouse row can show/hide its dependent fields independently.
 */
const isFieldVisible = (item, entry) =>
  DEPENDENT_PARENTS.includes(item.parentFieldId)
    ? item.dependentIds?.includes(entry?.[item.parentFieldId])
    : item.isDefaultVisible !== false;

export const isFieldRequired = (item, entry) =>
  DEPENDENT_PARENTS.includes(item.parentFieldId)
    ? isFieldVisible(item, entry)
    : Boolean(item.IsRequired);

/**
 * Message for a blank required field, built from the field's own title.
 *
 * The config's `hint` is placeholder text ("for example 6", "Select"), so using it as the
 * error text told the user the expected format instead of that the field was missing.
 */
export const getRequiredFieldMessage = (item) => {
  const title = String(item?.title ?? "").trim();
  if (title === "") return Constants.FIELD_REQUIRED_ERROR;

  const isChoice = [BackendFrontend.OPTIONS, BackendFrontend.DATE].includes(item?.type);
  const prefix = isChoice ? Constants.PLEASE_SELECT_PREFIX : Constants.PLEASE_ENTER_PREFIX;

  return `${prefix} ${title}`;
};

/**
 * The error for one field of one warehouse entry, or "" when it is valid. Shared with the
 * submit-time validation so the required-ness rules and the messages can't drift apart.
 */
export const getWarehouseFieldError = (item, entry) => {
  if (item?.questionId || item?.isReadOnly || !isFieldRequired(item, entry)) return "";

  const value = entry?.[item.id];

  // Blank is a missing required field, whatever the field's type — checked before the
  // type-specific rules so an empty number input can't read as an invalid one
  if (value === undefined || value === null || String(value).trim() === "") {
    return getRequiredFieldMessage(item);
  }

  // Quantities are whole items: no decimals, nothing at or below zero
  if (item?.type === BackendFrontend.NUMBER) {
    const numericValue = Number(value);
    if (!Number.isInteger(numericValue) || numericValue <= 0) return Constants.INVALID_VALUE;
  }

  return "";
};

/**
 * A saved allocation in a warehouse the user isn't assigned to. It is shown but can't be
 * changed, so it is also left out of client-side field validation and keeps its stored values.
 */
export const isWarehouseEntryLocked = (entry, equipmentId, warehouseOptions) =>
  Boolean(
    equipmentId &&
      warehouseOptions &&
      entry?.warehouse &&
      !warehouseOptions.some((option) => option?.id === entry.warehouse)
  );

export const sanitizeLocationInfoValue = (fieldId, value) => {
  if (!ALPHANUMERIC_LOCATION_FIELD_IDS.includes(fieldId)) return value;

  return String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");
};

/**
 * Location QR number is read-only and derived as `warehouseCode-{row}-{section}-{shelf}`.
 * The backend recomputes the same value on save, so this is a live preview only.
 */
export const buildLocationQRNumber = (warehouseCode, entry) =>
  [
    warehouseCode,
    sanitizeLocationInfoValue("equipmentRow", entry?.equipmentRow),
    sanitizeLocationInfoValue("equipmentSection", entry?.equipmentSection),
    sanitizeLocationInfoValue("equipmentShelf", entry?.equipmentShelf),
  ]
    .map((part) => String(part ?? "").trim())
    .filter((part) => part !== "")
    .join("-");

/**
 * Running allocation totals so the user can see how much of the equipment's total quantity
 * is already assigned to locations and how much is still left to place.
 */
export const getAllocationSummary = (warehouseEntries, totalQuantity) => {
  const total = Number(totalQuantity) || 0;
  const assigned = warehouseEntries.reduce((sum, entry) => sum + (Number(entry?.quantity) || 0), 0);

  return { total, assigned, remaining: total - assigned };
};

function WarehouseInfoTab({
  equipmentId,
  equipmentTypeId,
  warehouseEntries,
  setWarehouseEntries,
  quantityType,
  totalQuantity,
}) {
  const dispatch = useDispatch();
  const shouldFocusNewEntry = useRef(false);
  const [suggestions, setSuggestions] = useState({ key: "", locations: [], failed: false });
  const configData = useSelector((state) => state.config);
  const warehouseInfoFields = configData?.equipmentConfig?.properties?.warehouseInfo || [];

  const perWarehouseFields = useMemo(
    () => warehouseInfoFields.filter((item) => PER_WAREHOUSE_FIELD_IDS.includes(item.id)),
    [warehouseInfoFields]
  );

  const warehouseFieldDef = perWarehouseFields.find((item) => item.id === Common.WAREHOUSE);

  const warehouseCodeById = useMemo(
    () => new Map((warehouseFieldDef?.options || []).map((opt) => [opt?.id, opt?.code || ""])),
    [warehouseFieldDef?.options]
  );

  const getWarehouseCode = (warehouseId) => warehouseCodeById.get(warehouseId) || "";

  const isEntryLocked = (entry) =>
    isWarehouseEntryLocked(entry, equipmentId, warehouseFieldDef?.options);

  const hasEquipmentType = /^[a-f\d]{24}$/i.test(equipmentTypeId);
  const suggestionKey = JSON.stringify([equipmentTypeId, [...warehouseCodeById.keys()]]);

  // Suggestions are read-only and are never requested while editing equipment.
  useEffect(() => {
    if (equipmentId || !hasEquipmentType) return undefined;
    let active = true;
    const fetchSuggestions = async () => {
      try {
        const result = await dispatch(suggestedLocationsThunk(equipmentTypeId));
        if (!active) return;
        const succeeded = result.payload?.status === 200 && result.payload?.data?.status === true;
        setSuggestions({
          key: suggestionKey,
          locations:
            succeeded && Array.isArray(result.payload.data.data) ? result.payload.data.data : [],
          failed: !succeeded,
        });
      } catch {
        if (active) setSuggestions({ key: suggestionKey, locations: [], failed: true });
      }
    };
    fetchSuggestions();
    return () => {
      active = false;
    };
  }, [dispatch, equipmentId, equipmentTypeId, hasEquipmentType, suggestionKey]);

  const suggestionLabels =
    hasEquipmentType && suggestions.key === suggestionKey
      ? [
          ...new Set(
            suggestions.locations
              .filter((location) =>
                warehouseCodeById.has(location.warehouse?.[Constants.MONGOOSE_ID])
              )
              .map((location) =>
                buildLocationQRNumber(
                  getWarehouseCode(location.warehouse[Constants.MONGOOSE_ID]) ||
                    location.warehouse.code,
                  location
                )
              )
              .filter(Boolean)
          ),
        ]
      : [];
  const suggestionMessage = () => {
    if (!hasEquipmentType) return Constants.SUGGESTED_LOCATIONS_SELECT_TYPE;
    if (suggestions.key !== suggestionKey) return Constants.SUGGESTED_LOCATIONS_LOADING;
    if (suggestions.failed) return Constants.SUGGESTED_LOCATIONS_FAILED;
    return suggestionLabels.length
      ? Constants.SUGGESTED_LOCATIONS_DESCRIPTION
      : Constants.SUGGESTED_LOCATIONS_EMPTY;
  };

  const getOptions = (item, entry) => {
    if (item.parentFieldId === "") return item?.options;
    return item?.options?.filter((option) =>
      option?.isVisibleForOptions?.includes(entry[item.parentFieldId])
    );
  };

  const handleEntryChange = (index, name, value) => {
    setWarehouseEntries((prev) =>
      prev.map((entry, entryIndex) => {
        if (entryIndex !== index) return entry;

        const updated = { ...entry, [name]: sanitizeLocationInfoValue(name, value) };

        // Drop the error the user is currently fixing so a stale message can't sit under a
        // field that now has a value. The duplicate-position error is reported on the
        // warehouse field but derives from the whole slot, so any part of it clears that too.
        if (entry?.errors) {
          const { [name]: clearedError, ...remainingErrors } = entry.errors;
          updated.errors = POSITION_FIELD_IDS.includes(name)
            ? Object.fromEntries(
                Object.entries(remainingErrors).filter(([key]) => key !== Common.WAREHOUSE)
              )
            : remainingErrors;
        }

        // Drop the error the user is currently fixing so a stale message can't sit under a
        // field that now has a value. The duplicate-position error is reported on the
        // warehouse field but derives from the whole slot, so any part of it clears that too.
        if (entry?.errors) {
          const { [name]: clearedError, ...remainingErrors } = entry.errors;
          updated.errors = POSITION_FIELD_IDS.includes(name)
            ? Object.fromEntries(
                Object.entries(remainingErrors).filter(([key]) => key !== Common.WAREHOUSE)
              )
            : remainingErrors;
        }

        // Changing condition invalidates the dependent reason fields for this row only
        if (name === "condition") {
          updated.reasonStatus = "";
          updated.reason = "";
        }
        if (name === "reasonStatus") {
          updated.reason = "";
        }

        return updated;
      })
    );
  };

  const handleAddWarehouse = () => {
    shouldFocusNewEntry.current = true;
    setWarehouseEntries((prev) => [...prev, emptyWarehouseEntry()]);
  };

  // New locations are appended at the end, so bring the added one into view and focus it
  useEffect(() => {
    if (!shouldFocusNewEntry.current) return;
    shouldFocusNewEntry.current = false;

    const newEntry = document.getElementById(`warehouse-entry-${warehouseEntries.length - 1}`);
    newEntry?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    newEntry?.querySelector('[role="button"], input')?.focus({ preventScroll: true });
  }, [warehouseEntries.length]);

  const handleRemoveWarehouse = (index) => {
    setWarehouseEntries((prev) => prev.filter((_, entryIndex) => entryIndex !== index));
  };

  // Preselect the warehouse only when the account has just one to choose from
  useEffect(() => {
    if (equipmentId || !warehouseFieldDef) return;

    setWarehouseEntries((prev) => {
      if (prev.length !== 1 || prev[0].warehouse) return prev;

      const defaultWarehouse =
        warehouseFieldDef?.options?.length === 1 ? warehouseFieldDef?.options?.[0]?.id : "";

      if (!defaultWarehouse) return prev;

      return [{ ...prev[0], warehouse: defaultWarehouse }];
    });
  }, [equipmentId, quantityType, warehouseFieldDef?.options?.length]);

  const { total, assigned, remaining } = getAllocationSummary(warehouseEntries, totalQuantity);

  const allocationColor = () => {
    if (remaining === 0) return "#027A48";
    if (remaining < 0) return "#B42318";
    return "#667085";
  };

  const renderField = (item, entry, index) => {
    if (!isFieldVisible(item, entry)) return null;

    const required = isFieldRequired(item, entry);
    const label = required ? `${item.title}*` : item.title;
    // Derived on every render; the server recomputes the authoritative value on save
    const fieldValue =
      item.id === LOCATION_QR_FIELD_ID
        ? buildLocationQRNumber(getWarehouseCode(entry?.warehouse) || entry?.warehouseCode, entry)
        : entry[item.id] ?? "";
    const fieldError = entry?.errors?.[item.id];
    const locked = isEntryLocked(entry);

    // 4 fields per line so each warehouse still renders in two lines
    const columnWidth = 3;

    switch (item.type) {
      case BackendFrontend.TEXT:
        return (
          <Grid item lg={columnWidth} sm={12} key={item.id}>
            <FTextField
              label={label}
              placeholder={item?.hint}
              name={item?.id}
              id={`${item?.id}-${index}`}
              type="text"
              value={fieldValue}
              disabled={locked || Boolean(item?.isReadOnly)}
              error={Boolean(fieldError)}
              helperText={fieldError}
              handleChange={(e) => handleEntryChange(index, item.id, e.target.value)}
            />
          </Grid>
        );

      case BackendFrontend.OPTIONS: {
        const filteredOptions =
          locked && item.id === Common.WAREHOUSE
            ? [{ id: entry.warehouse, title: entry.warehouseLabel }]
            : getOptions(item, entry);
        return (
          <Grid item lg={columnWidth} sm={12} key={item.id}>
            <ConfigDropdown
              label={label}
              menu={filteredOptions?.map((val) => ({
                [Constants.MONGOOSE_ID]: val?.id,
                title: val?.title,
              }))}
              name={item?.id}
              id={`${item?.id}-${index}`}
              error={Boolean(fieldError)}
              helperText={fieldError}
              defaultValue=""
              disabled={locked}
              value={fieldValue}
              handleChange={(name, value) => handleEntryChange(index, name, value)}
            />
          </Grid>
        );
      }

      case BackendFrontend.DATE:
        return (
          <Grid item lg={columnWidth} sm={12} key={item.id}>
            <ReactDatePicker
              selected={
                (entry?.[item.id] ? moment(entry?.[item.id].split(".")[0]).toDate() : new Date()) ||
                null
              }
              onChange={(date) =>
                handleEntryChange(
                  index,
                  item.id,
                  moment(date).format(defaultData.DATABSE_DATE_FORMAT).toString()
                )
              }
              customInput={
                <DateTime
                  item={item}
                  value={fieldValue}
                  label={label}
                  errors={fieldError}
                  helperText={fieldError}
                  placeholder={Common.DATE_FORMAT_MM_DD_YYYY}
                />
              }
              placeholderText={Common.DATE_FORMAT_MM_DD_YYYY}
              dateFormat={defaultData.REACTDATETIMEPICKER_DATE_FORMAT}
            />
          </Grid>
        );

      case BackendFrontend.NUMBER:
        return (
          <Grid item lg={columnWidth} sm={12} key={item.id}>
            <FTextField
              label={label}
              placeholder={item?.hint}
              name={item?.id}
              id={`${item?.id}-${index}`}
              value={
                !equipmentId && item?.id === Common.QUANTITY && quantityType === true
                  ? 1
                  : fieldValue
              }
              type="number"
              disabled={locked || (item?.id === Common.QUANTITY && quantityType === true)}
              error={Boolean(fieldError)}
              helperText={fieldError}
              handleChange={(e) => handleEntryChange(index, item.id, e.target.value)}
            />
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <MDBox display="flex" flexDirection="column" gap={1}>
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <MDBox display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
          <MDTypography
            sx={{
              fontSize: pxToRem(20),
              fontWeight: 600,
              color: "#667085",
            }}
          >
            Location Info
          </MDTypography>
          {total > 0 && (
            <MDTypography sx={{ fontSize: pxToRem(14), fontWeight: 500, color: allocationColor() }}>
              {`Assigned ${assigned} of ${total} · ${
                remaining < 0 ? `Over-assigned by ${Math.abs(remaining)}` : `Remaining ${remaining}`
              }`}
            </MDTypography>
          )}
        </MDBox>
        {/* Hidden when there is nothing left to place: a unique quantity type is a single item
            that sits in one spot, and once the locations account for the whole total another
            location could only over-allocate. Still offered while the two disagree, including
            before a total has been entered. */}
        {quantityType !== true && remaining !== 0 && (
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            style={{ textTransform: "none", boxShadow: "none" }}
            onClick={handleAddWarehouse}
          >
            {Icons.NEW}&nbsp;Add Location
          </MDButton>
        )}
      </MDBox>

      {!equipmentId && (
        <MDBox my={1}>
          <MDTypography sx={{ fontSize: pxToRem(14), fontWeight: 600 }}>
            {Constants.SUGGESTED_LOCATIONS_TITLE}
          </MDTypography>
          <MDTypography sx={{ fontSize: pxToRem(14), color: "#667085" }} role="status">
            {suggestionMessage()}
          </MDTypography>
          <MDBox display="flex" flexWrap="wrap" gap={1} mt={0.5}>
            {suggestionLabels.map((label) => (
              <Chip key={label} label={label} size="small" variant="outlined" />
            ))}
          </MDBox>
        </MDBox>
      )}

      {warehouseEntries.map((entry, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <MDBox key={`warehouse-entry-${index}`} id={`warehouse-entry-${index}`} mb={1}>
          {index > 0 && <Divider sx={{ marginY: 1 }} />}

          <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
            <MDTypography
              sx={{ fontSize: pxToRem(14), fontWeight: 600, color: "#667085" }}
            >{`Location ${index + 1}`}</MDTypography>
            {warehouseEntries.length > 1 && !isEntryLocked(entry) && (
              <IconButton
                aria-label="remove location"
                color="error"
                onClick={() => handleRemoveWarehouse(index)}
              >
                {Icons.DELETE}
              </IconButton>
            )}
          </MDBox>

          <Grid container spacing={1} sx={{ display: "flex", justifyContent: "start" }}>
            {perWarehouseFields.map((item) => renderField(item, entry, index))}
          </Grid>
        </MDBox>
      ))}
    </MDBox>
  );
}

WarehouseInfoTab.propTypes = {
  warehouseEntries: PropTypes.arrayOf(PropTypes.object).isRequired,
  setWarehouseEntries: PropTypes.func.isRequired,
  equipmentTypeId: PropTypes.string,
  equipmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  quantityType: PropTypes.bool.isRequired,
  totalQuantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

WarehouseInfoTab.defaultProps = {
  equipmentTypeId: "",
  totalQuantity: 0,
};

export default WarehouseInfoTab;
